import { Group, Modal, Stack } from '@mantine/core';
import { useState, useCallback, useMemo } from 'react';
import { RemoteSourceSelect } from 'components/selects/RemoteSourceSelect';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import panic from 'errors/panic';
import { ChastiaConfig } from 'components/forms/remote-source/config/types';

/**
 * Parameters of the CopyRemoteSourceConfigModal component.
 */
interface CopyRemoteSourceConfigModalProps {
  opened: boolean;
  onClose: () => void;
  currentRemoteSourceId: number;
  onConfigSelected: (config: ChastiaConfig) => void;
}

/**
 * Modal for selecting a remote source to copy configuration from.
 */
export default function CopyRemoteSourceConfigModal({
  opened,
  onClose,
  currentRemoteSourceId,
  onConfigSelected,
}: CopyRemoteSourceConfigModalProps) {
  const { getAction } = useApi();
  const [selectedRemoteSourceId, setSelectedRemoteSourceId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const excludeRemoteSourceIds = useMemo(() => [currentRemoteSourceId], [currentRemoteSourceId]);

  const handleConfirm = useCallback(async () => {
    if (!selectedRemoteSourceId) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    try {
      setLoading(true);
      const remoteSourceGet = getAction('RemoteSourceGet');
      const remoteSource = await remoteSourceGet({ parameters: { remoteSourceId: selectedRemoteSourceId } });

      if (remoteSource.config) {
        const config: ChastiaConfig = JSON.parse(remoteSource.config);
        onConfigSelected(config);
        onClose();
      }
    } catch (err: any) {
      panic(err);
    } finally {
      setLoading(false);
    }
  }, [selectedRemoteSourceId, getAction, onConfigSelected, onClose]);

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title="Kopírovať konfiguráciu z iného externého zdroja"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
      size="md"
    >
      <Stack spacing={24}>
        <RemoteSourceSelect
          label="Vyberte externý zdroj, z ktorého chcete skopírovať konfiguráciu"
          size="lg"
          value={selectedRemoteSourceId}
          onChange={(value) => setSelectedRemoteSourceId(value || '')}
          showDiscarded={false}
          excludeRemoteSourceIds={excludeRemoteSourceIds}
        />

        <Group position="right" spacing={16}>
          <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading} disabled={!selectedRemoteSourceId}>
            Potvrdiť
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
